import { useMemo } from 'react';
import { Typography } from '@mui/material';

import Number from 'src/components/common/Number';
import Image from 'src/components/Image';
import { DYDX_TOKEN } from 'src/constants/tokens';
import type { Reward } from 'src/types/reward';
import type { Validator } from 'src/types/validator';
import { fix, gt, toDisplayDenomAmount } from 'src/utils/number';

import {
  AmountContainer,
  Base02TextContainer,
  ImageContainer,
  LeftColumnContainer,
  LeftColumnSubTitleContainer,
  LeftColumnSubTitleItemContainer,
  LeftColumnTitleContainer,
  LeftColumnTitleIconButton,
  LeftContainer,
  RightContainer,
  ValidatorButton,
} from './styled';

import DefaultValidatorIcon from 'src/assets/svg/DefaultValidator.svg';
import { ReactComponent as MintscanButtonIcon } from 'src/assets/svg/MintscanButton.svg';
import { ReactComponent as RightChevronIcon } from 'src/assets/svg/RightChevron.svg';

type ValidatorButtonItemProps = {
  validator?: Validator;
  leftTitle: string;
  leftTitleValue: string;
  rightTitle?: string;
  rightTitleValue?: Reward;
  isUndelegate?: boolean;
  onClickValidator: (isClicked: boolean) => void;
};

export default function ValidatorButtonItem({
  validator,
  leftTitle,
  leftTitleValue,
  rightTitle,
  rightTitleValue,
  isUndelegate = false,
  onClickValidator,
}: ValidatorButtonItemProps) {
  const displayLeftTitleValueAmount = useMemo(() => toDisplayDenomAmount(leftTitleValue, DYDX_TOKEN.decimals), [leftTitleValue]);

  const displayRightTitleValueAmount = useMemo(
    () => toDisplayDenomAmount(rightTitleValue?.reward.find((item) => item.denom === DYDX_TOKEN.denom)?.amount || '0', DYDX_TOKEN.decimals),
    [rightTitleValue],
  );

  return (
    <ValidatorButton
      data-is-disabled={isUndelegate}
      onClick={() => {
        if (!isUndelegate) {
          onClickValidator(true);
        }
      }}
    >
      <LeftContainer>
        <ImageContainer>
          <Image
            src={`https://raw.githubusercontent.com/cosmostation/chainlist/main/chain/dydx/moniker/${validator?.operator_address || ''}.png`}
            defaultImgSrc={DefaultValidatorIcon}
          />
        </ImageContainer>
        <LeftColumnContainer>
          <LeftColumnTitleContainer>
            <Typography variant="h4">{validator?.description.moniker || 'Validator'}</Typography>
            <LeftColumnTitleIconButton
              onClick={(e) => {
                e.stopPropagation();
                window.open(`https://www.mintscan.io/dydx/validators/${validator?.operator_address || ''}`);
              }}
            >
              <MintscanButtonIcon />
            </LeftColumnTitleIconButton>
          </LeftColumnTitleContainer>

          <LeftColumnSubTitleContainer>
            <LeftColumnSubTitleItemContainer>
              <Base02TextContainer>
                <Typography variant="h9">{leftTitle} : </Typography>
              </Base02TextContainer>
              &nbsp;
              <AmountContainer>
                <Number typoOfIntegers="h6" typoOfDecimals="h8">
                  {gt(displayLeftTitleValueAmount, '0')
                    ? gt(displayLeftTitleValueAmount, '0.000001')
                      ? String(parseFloat(fix(displayLeftTitleValueAmount, 6)))
                      : '< 0.000001'
                    : '0'}
                </Number>
              </AmountContainer>
            </LeftColumnSubTitleItemContainer>

            {rightTitle && (
              <LeftColumnSubTitleItemContainer>
                <Base02TextContainer>
                  <Typography variant="h9">{`${rightTitle} ${
                    gt(rightTitleValue?.reward.length || '0', '1') ? `+${rightTitleValue?.reward.length || '0'}` : ''
                  }:`}</Typography>
                </Base02TextContainer>
                &nbsp;
                <Number typoOfIntegers="h6" typoOfDecimals="h8">
                  {gt(displayRightTitleValueAmount, '0')
                    ? gt(displayRightTitleValueAmount, '0.000001')
                      ? String(parseFloat(fix(displayRightTitleValueAmount, 6)))
                      : '< 0.000001'
                    : '0'}
                </Number>
              </LeftColumnSubTitleItemContainer>
            )}
          </LeftColumnSubTitleContainer>
        </LeftColumnContainer>
      </LeftContainer>

      {!isUndelegate && (
        <RightContainer>
          <RightChevronIcon />
        </RightContainer>
      )}
    </ValidatorButton>
  );
}
